import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getComicById } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    genre: comic.categories,
    url: `https://hanmanhua.com/comic/${comic.id}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 5,
      ratingCount: Math.floor(comic.views / 35),
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanhua.com' },
      { '@type': 'ListItem', position: 2, name: comic.categories[0], item: `https://hanmanhua.com/category/${getCategorySlug(comic.categories[0])}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: comic.categories[0], href: `/category/${getCategorySlug(comic.categories[0])}` },
                  { label: comic.title },
                ]}
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-[280px] rounded-xl overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫免费看`}
                    className="w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  {comic.status === '连载中' ? (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-accent-500 text-white whitespace-nowrap">
                      连载中
                    </span>
                  ) : (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-500 text-white whitespace-nowrap">
                      已完结
                    </span>
                  )}
                  {comic.categories.map((cat) => (
                    <Link
                      key={cat}
                      to={`/category/${getCategorySlug(cat)}`}
                      className="px-3 py-1 text-xs font-medium rounded-full bg-background-200 text-foreground-600 hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat}
                    </Link>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-4 rounded-xl bg-background-100">
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground-900">{comic.rating}</div>
                    <div className="text-xs text-foreground-500 mt-1">评分</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground-900">
                      {comic.views > 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
                    </div>
                    <div className="text-xs text-foreground-500 mt-1">阅读量</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground-900">
                      {comic.followers > 10000 ? `${(comic.followers / 10000).toFixed(0)}万` : comic.followers}
                    </div>
                    <div className="text-xs text-foreground-500 mt-1">收藏</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground-900">{comic.chapters.length}话</div>
                    <div className="text-xs text-foreground-500 mt-1">总章节</div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2.5 text-sm text-foreground-600 mb-6">
                  <span className="text-foreground-500">作者：</span>
                  <span className="font-medium">{comic.author}</span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-500">画师：</span>
                  <span className="font-medium">{comic.artist}</span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-500">最近更新：</span>
                  <span className="font-medium">{comic.updatedAt}</span>
                </div>

                <div className="p-5 rounded-xl bg-background-100 mb-8">
                  <h2 className="text-base font-heading font-bold text-foreground-900 mb-3">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-wrap gap-3 mb-8">
                  <button className="px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-line mr-2"></i>
                    开始阅读
                  </button>
                  <button className="px-6 py-3 rounded-full border border-background-300 text-foreground-600 font-medium text-sm hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line mr-2"></i>
                    加入收藏
                  </button>
                </div>

                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">
                    章节列表
                    <span className="text-sm font-normal text-foreground-500 ml-2 font-body">共 {comic.chapters.length} 话</span>
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {[...comic.chapters].reverse().map((chapter) => (
                      <button
                        key={chapter.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer text-left group"
                      >
                        <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors truncate">
                          第{chapter.number}话 {chapter.title.replace(`第${chapter.number}话 `, '')}
                        </span>
                        <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.updatedAt}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}

function getCategorySlug(categoryName: string): string {
  const map: Record<string, string> = {
    '恋爱': 'romance',
    '奇幻': 'fantasy',
    '动作': 'action',
    '校园': 'school',
    '悬疑': 'mystery',
    '古风': 'historical',
    '剧情': 'drama',
    '惊悚': 'horror',
    '武侠': 'martial',
  };
  return map[categoryName] || 'all';
}